import { UserDetailsModalMutation } from '#app/__generated__/relay/UserDetailsModalMutation.graphql.js';
import { type UserDetailsModalUserProfileByUsernameQuery } from '#app/__generated__/relay/UserDetailsModalUserProfileByUsernameQuery.graphql.js';
import { ModalTemplateWrapper } from '#app/components/Modals/V2/GlobalModalSystem/ModalTemplateWrapper/ModalTemplateWrapper.js';
import { Text } from '#app/components/Primitives/Text.js';
import { TagListMenu } from '#app/components/Tags/TagListMenu/TagListMenu.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import customerioIcon from '#app/features/internal/icons/customerio.svg';
import googleLogExplorerIcon from '#app/features/internal/icons/google-log-explorer.svg';
import intercomIcon from '#app/features/internal/icons/intercom.svg';
import lookerIcon from '#app/features/internal/icons/looker.svg';
import posthogIcon from '#app/features/internal/icons/posthog.svg';
import sendgridIcon from '#app/features/internal/icons/sendgrid.svg';
import sentryIcon from '#app/features/internal/icons/sentry.svg';
import stripeIcon from '#app/features/internal/icons/stripe.svg';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useHasUserFeatures } from '#app/hooks/useHasUserFeatures.js';
import { logger } from '#app/services/logger.js';
import { graphql, useLazyLoadQuery } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { ReactNode, useCallback } from 'react';
import { styled } from 'styled-components';

const log = logger.child({
  namespace: 'UserDetailsModal',
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: calc(100vh - 120px);

  & > *:last-child {
    padding-bottom: 36px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 200px));
  gap: 16px;
`;

const Link = styled.a`
  ${({ href }) => `
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: center;
    ${
      href
        ? ''
        : `
      opacity: 0.5;
      pointer-events: none;
    `
    }
  `}
`;

const Icon = styled.img`
  width: 16px;
  height: 16px;
`;

const ListItem = ({
  children,
  href,
  icon,
}: {
  readonly children: ReactNode;
  readonly href?: string;
  readonly icon: string;
}) => {
  return (
    <Link
      href={href}
      target="_blank"
    >
      <Icon
        alt=""
        src={icon}
      />
      {children}
    </Link>
  );
};

type UserDetailsModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly username: string;
};

export const UserDetailsModal = ({
  isOpen,
  onClose,
  username,
}: UserDetailsModalProps) => {
  const toast = useToast();
  const canView = useHasUserFeatures(['SEARCH_USERS']);
  const { adminUserProfileByUsername: user } =
    useLazyLoadQuery<UserDetailsModalUserProfileByUsernameQuery>(
      graphql`
        query UserDetailsModalUserProfileByUsernameQuery($username: String!) {
          adminUserProfileByUsername(username: $username) {
            analyticsUserId
            databaseId
            emailAddress
            foreignStripeAccountId
            foreignStripeCustomerId
            foreignWorkspaceStripeCustomerId
            id
            intercomId
            userFeatures
            userGroups
            userInterfaceFlags
          }
        }
      `,
      {
        username,
      },
    );

  const [maybeCreateClientWorkspacePaymentAccount] =
    useContraMutation<UserDetailsModalMutation>(graphql`
      mutation UserDetailsModalMutation(
        $input: AdminMaybeCreateClientWorkspacePaymentAccountInput!
      ) {
        adminMaybeCreateClientWorkspacePaymentAccount(input: $input) {
          adminUserSearchResult {
            foreignWorkspaceStripeCustomerId
          }
          errors {
            message
            __typename
          }
        }
      }
    `);

  const onMaybeCreateClientWorkspacePaymentAccount = useCallback(() => {
    if (!user) {
      toast.warning('User not found.');
      return;
    }

    maybeCreateClientWorkspacePaymentAccount({
      onCompleted: (errors) => {
        const { apiErrors, payloadErrors } = errors;

        if (payloadErrors) {
          for (const payloadError of payloadErrors) {
            captureException(payloadError.message, {
              extra: {
                locations: payloadError.locations,
                severity: payloadError.severity,
              },
            });
            toast.error(
              'An unexpected error occurred. Our team has been notified.',
            );
          }

          return;
        }

        if (apiErrors) {
          for (const apiError of apiErrors) {
            toast.error(apiError.message);
            log.debug(apiError, apiError.message);
          }
        }
      },
      onError: (error) => {
        captureException(error);
        toast.error('Oops! Something went wrong. Our team has been notified.');
      },
      variables: {
        input: {
          userId: user.id,
        },
      },
    });
  }, [maybeCreateClientWorkspacePaymentAccount, toast, user]);

  if (!canView || !user) {
    return null;
  }

  return (
    <ModalTemplateWrapper
      ariaLabel="User details"
      desktopModalSize="lg"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Container>
        <Section>
          <Text textStyle="sixthLevelHeadline">Links</Text>
          <Grid>
            <ListItem
              href={`https://app.intercom.com/a/apps/de1o0crp/users/${user.intercomId}/all-conversations`}
              icon={intercomIcon}
            >
              Conversations
            </ListItem>
            <ListItem
              href={`https://fly.customer.io/workspaces/107768/journeys/people/${user.analyticsUserId}/sent`}
              icon={customerioIcon}
            >
              Emails
            </ListItem>
            <ListItem
              href={`https://app.sendgrid.com/email_activity?filters=%5B%7B%22val%22%3A%5B%22${user.emailAddress}%22%5D%2C%22selectedFieldName%22%3A%22to_email%22%2C%22comparisonType%22%3A%22Contains%22%7D%5D`}
              icon={sendgridIcon}
            >
              Email activity
            </ListItem>
            <ListItem
              href={`https://contra.sentry.io/issues/?environment=production&project=5302437&project=5277843&query=user.id%3A${user.databaseId}`}
              icon={sentryIcon}
            >
              Issues
            </ListItem>
            <ListItem
              href={`https://console.cloud.google.com/logs/query;query=resource.labels.container_name%3D%22contra-api%22%0AjsonPayload.context.userAccountId%3D%22${user.databaseId}%22?project=contrawork`}
              icon={googleLogExplorerIcon}
            >
              Logs
            </ListItem>
            <ListItem
              href={`https://us.posthog.com/person/${user.analyticsUserId}#activeTab=sessionRecordings`}
              icon={posthogIcon}
            >
              Recordings
            </ListItem>
            <ListItem
              href={`https://us.posthog.com/person/${user.analyticsUserId}#activeTab=featureFlags`}
              icon={posthogIcon}
            >
              Feature flags
            </ListItem>
            <ListItem
              href={`https://contra.cloud.looker.com/dashboards/66?User%20Account%20ID=${user.databaseId}`}
              icon={lookerIcon}
            >
              Data
            </ListItem>
            <ListItem
              href={
                user.foreignStripeAccountId
                  ? `https://dashboard.stripe.com/connect/accounts/${user.foreignStripeAccountId}/activity`
                  : undefined
              }
              icon={stripeIcon}
            >
              Connect account
            </ListItem>
            <ListItem
              href={
                user.foreignStripeCustomerId
                  ? `https://dashboard.stripe.com/customers/${user.foreignStripeCustomerId}`
                  : undefined
              }
              icon={stripeIcon}
            >
              Customer account
            </ListItem>
            <span>
              <ListItem
                href={
                  user.foreignWorkspaceStripeCustomerId
                    ? `https://dashboard.stripe.com/customers/${user.foreignWorkspaceStripeCustomerId}`
                    : undefined
                }
                icon={stripeIcon}
              >
                Workspace account
              </ListItem>
              {user.foreignWorkspaceStripeCustomerId ? null : (
                <button
                  onClick={onMaybeCreateClientWorkspacePaymentAccount}
                  type="button"
                >
                  🔄
                </button>
              )}
            </span>
          </Grid>
        </Section>
        <Section>
          <Text textStyle="sixthLevelHeadline">User Groups</Text>
          <TagListMenu
            items={user.userGroups.toSorted().map((name) => ({ name }))}
            maxRows={100}
            transformItemToTag={({ name }) => ({ label: name, value: name })}
            variant="condensed"
          />
        </Section>
        <Section>
          <Text textStyle="sixthLevelHeadline">User Features</Text>
          <TagListMenu
            items={user.userFeatures.toSorted().map((name) => ({ name }))}
            maxRows={100}
            transformItemToTag={({ name }) => ({ label: name, value: name })}
            variant="condensed"
          />
        </Section>
        {Boolean(user.userInterfaceFlags.length) && (
          <Section>
            <Text textStyle="sixthLevelHeadline">UI Flags</Text>
            <TagListMenu
              items={user.userInterfaceFlags
                .toSorted()
                .map((name) => ({ name }))}
              maxRows={100}
              transformItemToTag={({ name }) => ({
                label: name,
                value: name,
              })}
              variant="condensed"
            />
          </Section>
        )}
      </Container>
    </ModalTemplateWrapper>
  );
};
